/**
 * Thin wrapper over `rp_app.request` that always returns a Promise resolving to the parsed JSON body,
 * or rejecting with an Error whose `.code` (when present) matches the server's `error.code` field.
 *
 * Mirrors the contract of the PayPal smart-button `request.js` but with stricter error normalization:
 * the wallet sheet needs a hard distinction between "endpoint unavailable" (we should dismiss the
 * sheet) and "endpoint says try again" (we should resolve the event with an updated total).
 */
export default function request(url, { method = 'POST', data = {}, contentType = 'application/json', query = null } = {}) {
	const targetUrl = query ? appendQueryParams(url, query) : url;
	const isBodyMethod = method !== 'GET' && method !== 'HEAD';

	return new Promise((resolve, reject) => {
		window.rp_app.request({
			url: targetUrl,
			method,
			dataType: 'json',
			...(isBodyMethod
				? {
					contentType,
					data: contentType === 'application/json' ? JSON.stringify(data) : data,
				}
				: {}),
			error: (xhr) => {
				const status = typeof xhr?.status === 'number' ? xhr.status : 0;
				let body = null;
				try {
					body = xhr?.responseText ? JSON.parse(xhr.responseText) : null;
				} catch (parseError) {
					// Non-JSON error body (e.g. an HTML 500 page); fall back to null but surface the parse failure for debugging.
					console.warn(`Express checkout: could not parse error response from ${targetUrl}:`, parseError);
					body = null;
				}

				const err = new Error(
					body?.error?.message || `Request to ${targetUrl} failed with status ${status}`
				);
				err.status = status;
				err.code = body?.error?.code;
				err.body = body;
				reject(err);
			},
			success: (response) => {
				resolve(response);
			},
		});
	});
}

/**
 * Appends a flat key/value map to a URL's query string. Skips `null`/`undefined`/`''` so callers can
 * safely pass partial address objects (e.g. country alone before postal code is typed).
 *
 * @param {string} url
 * @param {Record<string, string | number | null | undefined>} params
 * @returns {string}
 */
function appendQueryParams(url, params) {
	const filtered = Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '');
	if (!filtered.length) {
		return url;
	}
	const search = filtered.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&');
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${search}`;
}
